import { HttpAction } from "@megaron/http-service";
import { Serializers, SerializerValue } from "@megaron/serializers";
import { uuidSerializer } from "@megaron/uuid";

import { pointCategorySerializer } from "./pointCategory";
import { transactionDtoSerializer } from "./transactionDto";

export const proStatusReasonSerializer = Serializers.stringOneOf("codeCount", "other");
export type ProStatusReason = SerializerValue<typeof proStatusReasonSerializer>;

export const myAccountDtoSerializer = Serializers.object({
  uuid: Serializers.string,
  balance: Serializers.decimal,
  isAffiliate: Serializers.boolean,
  unclaimedGiftPoints: Serializers.decimal,
  myAffiliateCodeIds: Serializers.array(Serializers.string),
  isPro: Serializers.boolean,
  proNumber: Serializers.integer.nullable(),
  lifetimeCodeCount: Serializers.integer,
  lifetimeConsumptionKg: Serializers.decimal,
});

export type MyAccountDto = SerializerValue<typeof myAccountDtoSerializer>;

export const myCustomerDtoSerializer = Serializers.object({
  userUuid: uuidSerializer,
  totalCommission: Serializers.decimal,
  invitedAt: Serializers.datetime,
  lastCommissionAt: Serializers.datetime.nullable(),
  affiliateCodeId: Serializers.string,
  myCustomerNumber: Serializers.integer,
  bonusProgress: Serializers.decimal.nullable(),
  bonusRequirement: Serializers.decimal.nullable(),
  isBonusCompleted: Serializers.boolean,
  bonusValue: Serializers.decimal,
});

export type MyCustomerDto = SerializerValue<typeof myCustomerDtoSerializer>;

export const accountDtoSerializer = Serializers.object({
  uuid: uuidSerializer,
  balance: Serializers.decimal,
  isAffiliate: Serializers.boolean,
  isPro: Serializers.boolean,
  proNumber: Serializers.integer.nullable(),
  lifetimeCodeCount: Serializers.integer,
  parentAffiliateUuid: uuidSerializer.nullable(),
});

export type AccountDto = SerializerValue<typeof accountDtoSerializer>;

const account = HttpAction({
  path: "/account/uuid/:uuid",
  method: "get",
  requiresAuth: true as const,
  paramsSerializer: Serializers.object({
    uuid: uuidSerializer,
  }),
  valueSerializer: accountDtoSerializer,
  errorSerializer: Serializers.stringOneOf("AccountNotFound"),
});

const myAccount = HttpAction({
  path: "/myAccount",
  method: "get",
  requiresAuth: true as const,
  valueSerializer: myAccountDtoSerializer,
});

const myTransactionHistory = HttpAction({
  path: "/myTransactionHistory",
  method: "get",
  requiresAuth: true as const,
  valueSerializer: Serializers.array(transactionDtoSerializer),
});

const myCustomers = HttpAction({
  path: "/myCustomers",
  method: "get",
  requiresAuth: true as const,
  valueSerializer: Serializers.array(myCustomerDtoSerializer),
});

const grantProStatus = HttpAction({
  requiresAuth: true as const,
  path: "/account/uuid/:accountUuid/grantProStatus",
  method: "post",
  paramsSerializer: Serializers.object({
    accountUuid: uuidSerializer,
  }),
});

const grantOverdueProStatuses = HttpAction({
  path: "/grantOverdueProStatuses",
  method: "post",
  requiresAuth: true as const,
  valueSerializer: Serializers.object({
    grantedCount: Serializers.integer,
  }),
});

const grantOverdueReferralBonuses = HttpAction({
  path: "/grantOverdueReferralBonuses",
  method: "post",
  requiresAuth: true as const,
  valueSerializer: Serializers.object({
    grantedCount: Serializers.integer,
  }),
});

const adjustBalance = HttpAction({
  path: "/account/uuid/:accountUuid/adjustBalance",
  method: "post",
  requiresAuth: true as const,
  paramsSerializer: Serializers.object({
    accountUuid: uuidSerializer,
  }),
  bodySerializer: Serializers.object({
    amount: Serializers.decimal,
    category: pointCategorySerializer,
  }),
});

export const accountActions = {
  account,
  myAccount,
  myTransactionHistory,
  myCustomers,
  grantProStatus,
  grantOverdueProStatuses,
  grantOverdueReferralBonuses,
  adjustBalance,
};
